import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/auth-provider";
import ScrollToTop from "@/components/ScrollToTop";
import { Suspense, lazy, useEffect, useState } from "react";
import LazyPageWrapper from "@/components/LazyPageWrapper";
import { routePreloader } from "@/services/RoutePreloader";
import { I18nProvider } from "@lingui/react";
import { i18n } from "@/i18n";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { DevDebugProvider } from "@/contexts/DevDebugContext";
import { DevDebugPanel } from "@/components/dev/DevDebugPanel";
import { DevDebugButton } from "@/components/dev/DevDebugButton";
import { ErrorBoundaryWrapper } from "@/components/dev/ErrorBoundaryWrapper";

// OpenAI Icons Sprite Loader
const OpenAIIconsSprite = () => {
  const [svgContent, setSvgContent] = useState<string>("");

  useEffect(() => {
    fetch("/openai-icons.svg")
      .then(res => res.text())
      .then(setSvgContent)
      .catch(console.error);
  }, []);

  if (!svgContent) return null;

  return (
    <div
      style={{ display: "none" }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const MyFolders = lazy(() => import("./pages/MyFolders"));
const HumanCoach = lazy(() => import("./pages/HumanCoach"));
const HumanMediator = lazy(() => import("./pages/HumanMediator"));
const HumanArbitrator = lazy(() => import("./pages/HumanArbitrator"));
const InvitationPage = lazy(() => import("./pages/InvitationPage"));
const Conversation = lazy(() => import("./pages/Conversation"));
const FolderMediatorRedirect = lazy(() => import("./pages/FolderMediatorRedirect"));
const FolderArbitratorRedirect = lazy(() => import("./pages/FolderArbitratorRedirect"));
const Legal = lazy(() => import("./pages/Legal"));
const Outils = lazy(() => import("./pages/Outils"));
const Experts = lazy(() => import("./pages/Experts"));

const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminFolders = lazy(() => import("./pages/AdminFolders"));
const AdminExperts = lazy(() => import("./pages/AdminExperts"));
const AdminUserDetail = lazy(() => import("./pages/AdminUserDetail"));
const AdminUserMessages = lazy(() => import("./pages/AdminUserMessages"));
const AdminAnonymousSessions = lazy(() => import("./pages/AdminAnonymousSessions"));
const AdminUsage = lazy(() => import("./pages/AdminUsage"));
const AdminPricing = lazy(() => import("./pages/AdminPricing"));
const JoinUs = lazy(() => import("./pages/JoinUs"));
const ExpertsDirectory = lazy(() => import("./pages/ExpertsDirectory"));
const ExpertDashboard = lazy(() => import("./pages/ExpertDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BrandTest = lazy(() => import("./pages/BrandTest"));
const Palette = lazy(() => import("./pages/Palette"));
const OpenAIIcons = lazy(() => import("./pages/OpenAIIcons"));
const VoiceTest = lazy(() => import("./pages/VoiceTest"));
const Version = lazy(() => import("./pages/Version"));

const queryClient = new QueryClient();

// Component to handle route preloading
const RoutePreloadManager = () => {
  const location = useLocation();

  useEffect(() => {
    // Preload critical routes on app start
    routePreloader.preloadCriticalRoutes();
  }, []);

  useEffect(() => {
    // Preload routes based on current location
    routePreloader.preloadBasedOnCurrentRoute(location.pathname);
  }, [location.pathname]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <DevDebugProvider>
        <I18nProvider i18n={i18n}>
          <LanguageProvider>
          <AuthProvider>
            <TooltipProvider>
              <OpenAIIconsSprite />
              <Toaster />
              <Sonner />
              <DevDebugPanel />
                <DevDebugButton />
              <BrowserRouter future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}>
                <ScrollToTop />
                <RoutePreloadManager />
                <ErrorBoundaryWrapper>
                  <LazyPageWrapper>
                    <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/my-folders" element={<MyFolders />} />
                    <Route path="/human-coach" element={<HumanCoach />} />
                    <Route path="/human-mediator" element={<HumanMediator />} />
                    <Route path="/human-arbitrator" element={<HumanArbitrator />} />
                    <Route path="/invitation/:interventionId" element={<InvitationPage />} />
                    <Route path="/conversation/:conversationId" element={<Conversation />} />
                    <Route path="/folder/:folderId/mediator" element={<FolderMediatorRedirect />} />
                    <Route path="/folder/:folderId/arbitrator" element={<FolderArbitratorRedirect />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/outils" element={<Outils />} />
                    <Route path="/experts" element={<Experts />} />
                    
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
                    <Route path="/admin/users/:userId/messages" element={<AdminUserMessages />} />
                    <Route path="/admin/folders" element={<AdminFolders />} />
                    <Route path="/admin/experts" element={<AdminExperts />} />
                    <Route path="/admin/anonymous-sessions" element={<AdminAnonymousSessions />} />
                    <Route path="/admin/usage" element={<AdminUsage />} />
                    <Route path="/admin/pricing" element={<AdminPricing />} />
                    <Route path="/admin/voice-test" element={<VoiceTest />} />
                    <Route path="/nous-rejoindre" element={<JoinUs />} />
                    <Route path="/nous-rejoindre/:inviteToken" element={<JoinUs />} />
                    <Route path="/annuaire-experts" element={<ExpertsDirectory />} />
                    <Route path="/expert" element={<ExpertDashboard />} />
                    <Route path="/brand-test" element={<BrandTest />} />
                    <Route path="/palette" element={<Palette />} />
                    <Route path="/openai-icons" element={<OpenAIIcons />} />
                    <Route path="/version" element={<Version />} />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </LazyPageWrapper>
                </ErrorBoundaryWrapper>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </LanguageProvider>
      </I18nProvider>
      </DevDebugProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
